/** biome-ignore-all lint/correctness/useHookAtTopLevel: not a problem */
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'
// biome-ignore lint/suspicious/noShadowRestrictedNames: not a problem
import { DataView } from 'primereact/dataview'
import { InputText } from 'primereact/inputtext'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DrawerWrapper } from '~/components/common/DrawerWrapper'
import { useLanguage } from '~/contexts/LanguageContext'
import {
  useControlDocumentLinkMutation,
  useControlDocumentRemoveLinkMutation,
} from '~/hooks/controlsApi'
import { useDepartmentsList } from '~/hooks/departmentsApi'
import { useDocumentList } from '~/hooks/documentsApi'
import { useUsersList } from '~/hooks/usersApi'
import { useUserStore } from '~/stores/userStore'
import type { ControlItem } from '~/types/controls.types'
import type { Document } from '~/types/document.types'
import { DocumentLiveStatus, DocumentStagingStatus } from '~/types/document.types'
import { UserRole } from '~/types/user.types'
import { CommentsDrawerContent } from './CommentsDrawer'
import DocumentCreateForm from './DocumentCreateForm'
import { DocumentDetailsDrawerContent } from './DocumentDetailsDrawer'

export type ControlStateType = ControlItem | false
export type ControlsDocumentDrawerProps = {
  control: ControlStateType
  hide: () => void
}

const StagingStatus = ({ status }: { status: DocumentStagingStatus }) => {
  switch (status) {
    case DocumentStagingStatus.PendingUpload:
      return <Chip label='Pending Upload' className='text-nowrap text-sm font-bold bg-red-300' />
    case DocumentStagingStatus.Draft:
      return <Chip label='Draft' className='text-nowrap text-sm font-bold bg-red-300' />
    case DocumentStagingStatus.InternalReview:
      return <Chip label='Internal Review' className='text-nowrap text-sm font-bold bg-amber-300' />
    case DocumentStagingStatus.ConsultantReview:
      return (
        <Chip label='Consultant Review' className='text-nowrap text-sm font-bold bg-cyan-300' />
      )
    case DocumentStagingStatus.AuditReady:
      return <Chip label='Audit Ready' className='text-nowrap text-sm font-bold bg-lime-300' />
    case DocumentStagingStatus.Final:
      return <Chip label='Final' className='text-nowrap text-sm font-bold bg-blue-300' />
  }
  return <Chip className='text-sm font-bold' label='Unknown Staging Status' />
}

const LiveStatus = ({ status }: { status: DocumentLiveStatus }) => {
  switch (status) {
    case DocumentLiveStatus.Staging:
      return <Chip label='Staging' className='text-nowrap text-sm font-bold bg-rose-300' />
    case DocumentLiveStatus.Live:
      return <Chip label='Live' className='text-nowrap text-sm font-bold bg-blue-300' />
    case DocumentLiveStatus.Archived:
      return <Chip label='Archived' className='text-nowrap text-sm font-bold bg-zinc-300' />
  }
  return <Chip label='Unknown Live Status' />
}

const renderDocument = (
  d: NonNullable<ControlItem['documents']>[0],
  control: ControlItem,
  lang: string,
  onSelect: (id: number) => void,
  onComment: (d: NonNullable<ControlItem['documents']>[0]) => void,
) => {
  const user = useUserStore()
  const departmentsList = useDepartmentsList()
  const userList = useUsersList()
  const removeLinkMutation = useControlDocumentRemoveLinkMutation()

  return (
    <div className='flex w-full justify-between items-center px-4 my-1 py-2 border rounded-lg hover:bg-gray-50 transition-colors'>
      <button
        type='button'
        tabIndex={0}
        className='cursor-pointer grow text-left'
        onClick={() => onSelect(d.id)}
      >
        <h2 className='text-lg font-bold'>
          {(lang === 'id' && d.titleId ? d.titleId : d.title) ?? 'Fetching title...'}
        </h2>
        <div className='flex gap-1 mt-1'>
          <Chip
            className='text-[10px] font-bold h-5'
            label={`Dept: ${departmentsList.data?.find((dp) => dp.id === d.departmentId)?.title ?? d.departmentId}`}
          />
          <Chip
            className='text-[10px] font-bold h-5'
            label={`PIC: ${userList.data?.find((u) => u.id === d.picUserId)?.name ?? d.picUserId}`}
          />
        </div>
      </button>
      <div className='flex flex-col items-end gap-1'>
        <StagingStatus status={d.stagingStatus} />
        <LiveStatus status={d.liveStatus} />
        <div className='flex items-center gap-1'>
          {(user.user?.role ?? 0) >= UserRole.Admin && (
            <Button
              icon='pi pi-trash'
              severity='danger'
              className='p-1 h-6 w-6'
              onClick={(e) => {
                e.stopPropagation()
                removeLinkMutation.mutate(
                  { controlId: control.id, documentId: d.id },
                  {
                    onSuccess: () => {
                      control.documents = (control.documents ?? []).filter((cd) => cd.id !== d.id)
                    },
                  },
                )
              }}
            />
          )}
          <Button
            icon='pi pi-comments'
            rounded
            text
            className='h-7 w-7'
            onClick={(e) => {
              e.stopPropagation()
              onComment(d)
            }}
          />
        </div>
      </div>
    </div>
  )
}

const DocumentLink = ({ control, onCancel }: { control: ControlItem; onCancel: () => void }) => {
  const { lang } = useLanguage()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const { data, isLoading } = useDocumentList({
    search,
    page,
    pageSize: 10,
    includeControls: true,
    liveStatuses: [DocumentLiveStatus.Live, DocumentLiveStatus.Staging],
  })
  const linkMutation = useControlDocumentLinkMutation()

  const handleLink = (documentId: number) => {
    linkMutation.mutate({ controlId: control.id, documentId }, { onSuccess: () => onCancel() })
  }

  const isLinked = (doc: Document) => {
    return doc.controlDocumentMap?.some((m) => m.controlId === control.id)
  }

  return (
    <div className='p-4 border border-blue-200 bg-blue-50 rounded-lg mb-4'>
      <h4 className='font-bold text-lg mb-2'>Link Existing Document</h4>
      <div className='flex gap-2 mb-2'>
        <InputText
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder='Search documents...'
          className='w-full h-9'
        />
        <Button label='Cancel' onClick={onCancel} severity='secondary' className='h-9' />
      </div>
      {isLoading && <p className='text-xs italic'>Searching...</p>}
      <DataView
        value={data?.data}
        itemTemplate={(doc: Document) => (
          <div className='flex justify-between items-center p-2 border-b text-sm'>
            <span>{lang === 'id' && doc.titleId ? doc.titleId : doc.title}</span>
            <Button
              icon='pi pi-link'
              disabled={isLinked(doc) || linkMutation.isLoading}
              onClick={() => handleLink(doc.id)}
              className='h-7 w-7'
            />
          </div>
        )}
        emptyMessage='No documents found.'
      />
      <div className='flex justify-center items-baseline gap-1 mt-2'>
        <Button
          icon='pi pi-angle-left'
          link
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0 || isLoading}
        />
        <span className='text-xs'>Page {page + 1}</span>
        <Button
          icon='pi pi-angle-right'
          link
          onClick={() => setPage((p) => p + 1)}
          disabled={!data || data.count <= (page + 1) * 10 || isLoading}
        />
      </div>
    </div>
  )
}

const DocumentCreate = ({ control, onCancel }: { control: ControlItem; onCancel: () => void }) => {
  return (
    <div className='p-4 border border-green-200 bg-green-50 rounded-lg mb-4'>
      <div className='flex justify-between items-baseline mb-2'>
        <h4 className='font-bold text-lg'>Create New Document</h4>
        <Button icon='pi pi-times' link onClick={onCancel} className='p-0 h-6 w-6' />
      </div>
      <DocumentCreateForm controlId={control.id} onSuccess={onCancel} onCancel={onCancel} />
    </div>
  )
}

const DrawerContent = ({ control, hide }: Omit<ControlsDocumentDrawerProps, 'lang'>) => {
  const [searchParams] = useSearchParams()
  const [createMode, setCreateMode] = useState<'create' | 'link' | undefined>()
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null)
  const [commentTarget, setCommentTarget] = useState<Document | null>(null)
  const user = useUserStore()
  const { lang } = useLanguage()

  const documentIdParam = searchParams.get('documentId')
  const showCommentsParam = searchParams.get('showComments')

  useEffect(() => {
    if (documentIdParam && control) {
      const docId = Number.parseInt(documentIdParam, 10)
      if (!Number.isNaN(docId)) {
        if (showCommentsParam === 'true') {
          const doc = (control as ControlItem).documents?.find((d) => d.id === docId)
          if (doc) {
            setCommentTarget(doc as unknown as Document)
          }
        } else {
          setSelectedDocId(docId)
        }
      }
    }
  }, [documentIdParam, showCommentsParam, control])

  if (!control) return null

  if (selectedDocId) {
    return (
      <DocumentDetailsDrawerContent
        documentId={selectedDocId}
        onBack={() => setSelectedDocId(null)}
        onHide={hide}
      />
    )
  }

  if (commentTarget) {
    return (
      <CommentsDrawerContent
        document={commentTarget}
        control={control}
        onBack={() => setCommentTarget(null)}
      />
    )
  }

  return (
    <div className='flex flex-col h-full bg-white p-4 overflow-hidden'>
      {/* Header */}
      <div className='flex items-center justify-between mb-4 shrink-0 border-b pb-2'>
        <div className='flex flex-col'>
          <h2 className='text-xl font-bold truncate max-w-[400px]'>
            {lang === 'id' && control.titleId ? control.titleId : control.title}
          </h2>
          <span className='text-xs text-gray-400 font-bold uppercase tracking-wider'>
            Linked Documents
          </span>
        </div>
        <div className='flex items-center gap-1'>
          {(user.user?.role ?? 0) >= UserRole.Admin && (
            <div className='flex gap-1 mr-2'>
              <Button
                icon='pi pi-plus'
                label='New'
                onClick={() => setCreateMode('create')}
                className='p-button-sm p-button-text'
              />
              <Button
                icon='pi pi-link'
                label='Link'
                onClick={() => setCreateMode('link')}
                className='p-button-sm p-button-text'
              />
            </div>
          )}
          <Button icon='pi pi-times' link onClick={hide} className='p-0 h-8 w-8 text-gray-400' />
        </div>
      </div>

      <div className='grow overflow-y-auto'>
        {createMode === 'create' && (
          <DocumentCreate control={control} onCancel={() => setCreateMode(undefined)} />
        )}
        {createMode === 'link' && (
          <DocumentLink control={control} onCancel={() => setCreateMode(undefined)} />
        )}

        <DataView
          value={control.documents}
          itemTemplate={(d) =>
            renderDocument(d, control, lang, setSelectedDocId, (doc) =>
              setCommentTarget(doc as unknown as Document),
            )
          }
          emptyMessage='No available documents'
        />
      </div>
    </div>
  )
}

const ControlsDocumentDrawer = (props: ControlsDocumentDrawerProps) => {
  return (
    <DrawerWrapper
      onHide={props.hide}
      visible={!!props.control}
      position='right'
      className='w-full max-w-160'
      blockScroll
    >
      <DrawerContent {...props} />
    </DrawerWrapper>
  )
}

export default ControlsDocumentDrawer
